import { DependencyList, FormEvent, useCallback, useState } from 'react';
import { captureError } from './errorHandling';

/**
 * The return type for the {@link useForm} hook.
 *
 * @property {boolean} saving Whether the form is currently saving.
 * @property onSubmit The callback to be called when the form is submitted.
 */
interface FormSetup {
  saving: boolean;
  onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
}

/**
 * A hook that simplifies form handling by providing a callback to be called when the form is submitted,
 * and a boolean indicating whether the form is currently saving.
 *
 * @param callback The callback to be called when the form is submitted. This will include the {@link FormEvent<HTMLFormElement>} event as the property `e`.
 * @param deps The dependencies for the callback.
 * @returns A {@link FormSetup} object that contains the `saving` boolean and the `onSubmit` function.
 *
 * @example
 * ```tsx
 * const MyComponent: FC = () => {
 *  const { onSubmit, saving } = useForm(async () => {
 *   // Run API calls or additional handling logic here
 *  }, []);
 *
 *  return (
 *   <form onSubmit={onSubmit}>
 *    <button type="submit" disabled={saving}>Submit</button>
 *   </form>
 *  );
 * };
 * ```
 */
export function useForm(callback: () => Promise<void>, deps: DependencyList): FormSetup {
  const [saving, setSaving] = useState(false);
  const onSubmit = useCallback(async (e?: FormEvent<HTMLFormElement>) => {
    setSaving(true);
    try {
      e?.preventDefault();
      await callback();
    } catch (error) {
      captureError(error as Error);
    }

    setSaving(false);
  }, deps);

  return { onSubmit, saving };
}
